/**
 * Segmentation validation utilities
 * Used to validate segmentation configurations in specific contexts
 */

import type { FlowStructureDto } from '@/types/flow-structure';

const PERIODIC_LAUNCHING_TYPE = 'boxperiodiclaunching';
const CONTACT_SEGMENTATION_TYPE = 'boxcontactseg';
/** Output index used by boxcontactseg for the "NO / unmatched" branch */
const CONTACT_SEGMENTATION_NO_OUTPUT = 2;

/**
 * Exact date operators that indicate a precise date condition.
 * Only these operators are considered valid for periodic trigger validation.
 *
 * - dateString_equals: exact date match
 * - dateNumber_x_units_ago_exactly / dateNumber_exactly_in_x_units: exactly X units ago/from now
 * - anniversaryNumber_x_units_ago_exactly / anniversaryNumber_exactly_in_x_units: anniversary exactly X units ago/from now
 */
const EXACT_DATE_OPERATORS = [
  'dateString_equals',
  'dateNumber_x_units_ago_exactly',
  'dateNumber_exactly_in_x_units',
  'anniversaryNumber_x_units_ago_exactly',
  'anniversaryNumber_exactly_in_x_units',
];

/**
 * Check if an operator is an exact date operator
 */
function isDateOperator(operator: string | undefined): boolean {
  if (!operator) return false;
  return EXACT_DATE_OPERATORS.includes(operator);
}

/**
 * Recursively check if a block or its sub_properties contain a date operator
 */
function blockOrSubPropertiesContainDate(block: any): boolean {
  // Check the block itself
  if (isDateOperator(block?.operator)) {
    return true;
  }

  // Check sub_properties recursively
  if (block?.sub_properties && Array.isArray(block.sub_properties)) {
    return block.sub_properties.some((subProp: any) => blockOrSubPropertiesContainDate(subProp));
  }

  return false;
}

export interface SegmentationDateValidationResult {
  valid: boolean;
  message?: string;
  messageParams?: Record<string, string | number>;
}

/**
 * Validate that segmentation config has at least one date condition per AND block.
 * Each OR block within an AND block must also have a date condition.
 *
 * This is required when the segmentation is used after a periodic launching trigger,
 * to ensure that the number of contacts passing the filter is limited.
 *
 * @param segmentationConfig The segmentation configuration [[OR blocks], [OR blocks], ...]
 * @returns Validation result with valid flag and optional error message
 */
export function validateSegmentationHasDateConditions(
  segmentationConfig: any[][],
): SegmentationDateValidationResult {
  if (!segmentationConfig || !Array.isArray(segmentationConfig)) {
    return { valid: false, message: 'automatedScenarios.validation.segmentation.invalidConfig' };
  }

  // Check if config is empty or only contains empty blocks
  const hasValidBlocks = segmentationConfig.some(
    (andBlock) => Array.isArray(andBlock) && andBlock.some((orBlock) => orBlock?.id),
  );

  if (!hasValidBlocks) {
    return {
      valid: false,
      message: 'automatedScenarios.validation.segmentation.missingDateCondition',
    };
  }

  // For each AND block, check if it contains at least one date condition
  const andBlocksWithDate: boolean[] = segmentationConfig.map(
    (andBlock) => Array.isArray(andBlock) && andBlock.some(
      (orBlock) => orBlock?.id && blockOrSubPropertiesContainDate(orBlock),
    ),
  );

  // Rule 1: At least one AND block must have a date condition
  const hasAtLeastOneAndBlockWithDate = andBlocksWithDate.some((hasDate) => hasDate);

  if (!hasAtLeastOneAndBlockWithDate) {
    return {
      valid: false,
      message: 'automatedScenarios.validation.segmentation.missingDateCondition',
    };
  }

  // Rule 2: For AND blocks with multiple OR blocks that don't have a date,
  // each OR must have a date (unless another AND block already has a date)
  const numberOfAndBlocksWithDate = andBlocksWithDate.filter((hasDate) => hasDate).length;

  for (let andIndex = 0; andIndex < segmentationConfig.length; andIndex += 1) {
    const andBlock = segmentationConfig[andIndex];
    const thisAndBlockHasDate = andBlocksWithDate[andIndex];

    // If this AND block doesn't have a date, but another AND block does, it's OK
    if (!thisAndBlockHasDate && numberOfAndBlocksWithDate > 0) {
      // Another AND block has a date, so the AND logic will filter contacts
      // No need to check OR blocks in this AND block
      // eslint-disable-next-line no-continue
      continue;
    }

    // If this AND block has multiple OR blocks, each must have a date
    if (Array.isArray(andBlock) && andBlock.length > 1) {
      for (let orIndex = 0; orIndex < andBlock.length; orIndex += 1) {
        const orBlock = andBlock[orIndex];
        if (orBlock?.id && !blockOrSubPropertiesContainDate(orBlock)) {
          return {
            valid: false,
            message: 'automatedScenarios.validation.segmentation.orBlockMissingDate',
            messageParams: { block: `${andIndex + 1}.${orIndex + 1}` },
          };
        }
      }
    }
  }

  return { valid: true };
}

/**
 * A node is "empty" when it (or one of its descendants) is an unconfigured
 * condition — a blank leaf persisted as `{}` / `{"id":""}`, at any depth
 * (e.g. a "Select a property" row inside a group). A group only carries the
 * emptiness of its children: a bare existence group (`{id, sub_properties:[]}`)
 * is a valid condition. Mirrors the `orBlock?.id` "real condition" convention.
 */
function nodeHasEmptyCondition(node: any): boolean {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return true;
  }
  if (Array.isArray(node.sub_properties)) {
    return node.sub_properties.some((child: any) => nodeHasEmptyCondition(child));
  }
  return !node.id;
}

/**
 * True when the config contains at least one unconfigured condition: an empty
 * AND-block, or any blank OR-condition (top-level or nested). Such a node
 * reaches the segment engine as a non-evaluable block that has to be stripped
 * (and previously crashed it), so the editor must not let it be saved.
 */
export function segmentationHasEmptyCondition(config: any): boolean {
  if (!Array.isArray(config) || config.length === 0) {
    return false;
  }
  return config.some((andBlock) => {
    if (!Array.isArray(andBlock) || andBlock.length === 0) {
      return true;
    }
    return andBlock.some((orBlock: any) => nodeHasEmptyCondition(orBlock));
  });
}

/**
 * Determine whether a step is on a "periodic chain" that requires an exact-date filter.
 *
 * A boxcontactseg filter needs a date condition when:
 *  - one of its parents is a periodic launching trigger, OR
 *  - one of its parents is itself a boxcontactseg connected via the NO output (2),
 *    and that parent is on a periodic chain (recursive).
 *
 * Visiting the NO branch only matches the runtime semantics: a child plugged on the
 * YES branch already inherits a bounded set of contacts and does not need its own date.
 *
 * @param stepId The step whose periodic-chain membership we want to assess
 * @param flow The full flow structure (steps + connections)
 * @returns true if the step inherits an unbounded contact volume that requires a date filter
 */
export function isOnPeriodicChain(
  stepId: string,
  flow: FlowStructureDto | undefined | null,
): boolean {
  if (!flow || !Array.isArray(flow.steps)) return false;

  const stepsById = new Map(flow.steps.map((s) => [s.id, s]));
  const visited = new Set<string>();

  const walk = (currentId: string): boolean => {
    if (visited.has(currentId)) return false;
    visited.add(currentId);

    const step = stepsById.get(currentId);
    if (!step) return false;

    let parents = step.parents && step.parents.length > 0 ? step.parents : [];
    if (parents.length === 0 && step.primaryParent) {
      parents = [step.primaryParent];
    }

    return parents.some((parentRef) => {
      const parentStep = stepsById.get(parentRef.id);
      if (!parentStep) return false;

      if (parentStep.type === PERIODIC_LAUNCHING_TYPE) {
        return true;
      }

      if (
        parentStep.type === CONTACT_SEGMENTATION_TYPE
        && parentRef.output === CONTACT_SEGMENTATION_NO_OUTPUT
      ) {
        return walk(parentStep.id);
      }

      return false;
    });
  };

  return walk(stepId);
}

const VISIT_DURATION_OPERATORS = ['number_greater_than_or_equal_to', 'number_greater_than'];

/**
 * Error of a « Visité des URLs » condition in an on-site trigger segmentation,
 * as an i18n key, or null. A visit duration is only measured on the current
 * session and can only be awaited while the page is read (« au moins » /
 * « plus de »), never with « zéro fois »; a session criterion takes no visit
 * date and at most one number of views and one duration.
 */
function triggerVisitCriterionError(block: any): string | null {
  if (block?.id !== 'visited_urls' || !Array.isArray(block.sub_properties)) {
    return null;
  }
  const subs = block.sub_properties.filter(Boolean);
  const inSession = subs.some(
    (sub: any) => sub.id === 'in_current_session' && (sub.data === true || sub.data === 'true'),
  );
  const durations = subs.filter((sub: any) => sub.id === 'time_on_page');
  const views = subs.filter((sub: any) => sub.id === 'nb_views');

  if (durations.length > 0 && !inSession) {
    return 'automatedScenarios.validation.segmentation.visitDurationNeedsSession';
  }
  if (durations.some((sub: any) => !VISIT_DURATION_OPERATORS.includes(sub.operator))) {
    return 'automatedScenarios.validation.segmentation.visitDurationAtLeast';
  }
  if (durations.length > 0 && views.some((sub: any) => sub.operator === 'zero_times')) {
    return 'automatedScenarios.validation.segmentation.visitDurationZeroViews';
  }
  if (inSession && (durations.length > 1 || views.length > 1 || subs.some((sub: any) => sub.id === 'url_visited_date'))) {
    return 'automatedScenarios.validation.segmentation.visitSessionCriterion';
  }
  return null;
}

/** First error of the « Visité des URLs » conditions of a trigger segmentation, or null. */
export function triggerSegmentationVisitError(config: any): string | null {
  if (!Array.isArray(config)) {
    return null;
  }
  for (let andIndex = 0; andIndex < config.length; andIndex += 1) {
    const andBlock = config[andIndex];
    if (Array.isArray(andBlock)) {
      for (let orIndex = 0; orIndex < andBlock.length; orIndex += 1) {
        const error = triggerVisitCriterionError(andBlock[orIndex]);
        if (error) {
          return error;
        }
      }
    }
  }
  return null;
}
